import { useCallback, useEffect, useState } from 'react';
import { promotionApi } from '../api/promotionApi';
import { clearPromoCampaign, getRememberedPromoCampaign } from '../promoCampaign';
import { PromoCampaign } from '../types';

/** One finished lookup, tagged with the slug and attempt it was made for so a stale result is never used. */
interface Lookup {
  key: string;
  campaign: PromoCampaign | null;
  failed: boolean;
}

export interface PromoCampaignState {
  /** The open campaign the golfer arrived through, or `null` when there is none (or it has ended). */
  campaign: PromoCampaign | null;
  /** True while the campaign is being looked up, on the first visit and again after a retry. */
  loading: boolean;
  /** True when the last lookup failed, so it is not known whether the offer still stands. */
  failed: boolean;
  /** Looks the campaign up again, e.g. after a failure or once the API turns a sign-up down because the offer just ended. */
  refresh: () => void;
  /** Gives up on the offer for this visit and forgets it on this device. */
  skip: () => void;
}

/**
 * The promo campaign this golfer arrived through (a QR code or shared link), if it is still open.
 * The link remembered in this browser wins; otherwise `claimedSlug` is the campaign recorded on the
 * golfer's account when they signed in, which is how the offer follows them to another browser.
 * A campaign the API no longer offers is forgotten. A failed lookup is reported as `failed` rather
 * than as "no offer", so the page can ask the golfer to retry instead of quietly selling them the
 * paid plan.
 */
export function usePromoCampaign(claimedSlug?: string | null): PromoCampaignState {
  const [remembered] = useState(getRememberedPromoCampaign);
  const slug = remembered ?? claimedSlug ?? null;
  const [attempt, setAttempt] = useState(0);
  const [skipped, setSkipped] = useState(false);
  const [lookup, setLookup] = useState<Lookup | null>(null);

  // A lookup only counts for the slug and attempt it was made for. Deriving "loading" from that,
  // rather than setting it from the effect, means there is no render in which a campaign that has
  // just become known (e.g. recorded on the account) looks like "nothing to look up".
  const key = slug && !skipped ? `${slug}#${attempt}` : null;
  const settled = lookup !== null && lookup.key === key ? lookup : null;

  useEffect(() => {
    if (!slug || skipped) return undefined;
    let cancelled = false;
    const lookupKey = `${slug}#${attempt}`;
    promotionApi.getCampaign(slug)
      .then((found) => {
        if (cancelled) return;
        if (found?.active) {
          setLookup({ key: lookupKey, campaign: found, failed: false });
        } else {
          clearPromoCampaign();
          setLookup({ key: lookupKey, campaign: null, failed: false });
        }
      })
      .catch(() => {
        if (!cancelled) setLookup({ key: lookupKey, campaign: null, failed: true });
      });
    return () => {
      cancelled = true;
    };
  }, [slug, attempt, skipped]);

  const refresh = useCallback(() => setAttempt((count) => count + 1), []);
  const skip = useCallback(() => {
    clearPromoCampaign();
    setSkipped(true);
  }, []);

  return {
    campaign: settled?.campaign ?? null,
    loading: key !== null && settled === null,
    failed: settled?.failed ?? false,
    refresh,
    skip,
  };
}
